import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ThemeManagerProps,
  ViewMode,
  EditorMode,
  SettingsSection,
  SelectedFont,
  SelectedIcon,
} from "./types";
import {
  useWidgetThemeParams,
  type WidgetThemeParams,
} from "../../utils/useWidgetThemeParams";
import { Button } from "../../components/Button";
import {
  Monitor,
  Smartphone,
  Settings,
  LogOut,
  Palette,
  Loader,
  Eye,
} from "lucide-react";
import {
  clearThemeParams,
  clearUrlParams,
  syncUrlToStorage,
} from "../../utils/themeStorage";
import { subscribeToUploadIconRequest } from "../../utils/editPanelEvents";
import {
  useWidgetTheme,
  useThemeColors,
  useThemeFonts,
  useThemeIcons,
} from "../../hooks/useWidgetTheme";
import { validateEditThemeToken } from "../../api/widgetThemeApi";
import { getThemeEditorStyles } from "./styles";
import { getIsFrameMode, canUseIframe } from "./utils";
import { useThemeHandlers } from "./useThemeHandlers";
import { SettingsPanel } from "./SettingsPanel";
import { ColorSection } from "./ColorSection";
import { FontSection } from "./FontSection";
import { IconSection } from "./IconSection";
import { FontPickerModal } from "./FontPickerModal";
import { IconPickerModal } from "./IconPickerModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";

interface ThemeManagerContentProps {
  themeParams: WidgetThemeParams;
  isFrameMode: boolean;
}

const ThemeManagerContent: React.FC<ThemeManagerContentProps> = ({
  themeParams,
  isFrameMode,
}) => {
  // Editor mode state - determines if we use iframe or inline mode
  const [editorMode, setEditorMode] = useState<EditorMode>("checking");

  // Viewport state
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const { data: theme } = useWidgetTheme();
  const themeName = theme?.themeName || themeParams.themeId || themeParams.version;
  const { themeColors, refreshThemeColors } = useThemeColors();
  const { themeFonts, refreshThemeFonts } = useThemeFonts();
  const { themeIcons, refreshThemeIcons } = useThemeIcons();

  const { data: _tmThemeData } = useWidgetTheme();
  const studioLockedColorKeys = useMemo(() => {
    const keys = (_tmThemeData as any)?.studioControlledKeys?.colors as string[] | undefined;
    return keys?.length ? new Set(keys) : undefined;
  }, [_tmThemeData]);
  const studioLockedFontKeys = useMemo(() => {
    const keys = (_tmThemeData as any)?.studioControlledKeys?.fonts as string[] | undefined;
    return keys?.length ? new Set(keys) : undefined;
  }, [_tmThemeData]);

  // Accordion state for settings sections (null = all collapsed)
  const [expandedSection, setExpandedSection] = useState<SettingsSection>(null);

  // Modal states
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const toggleSection = (section: SettingsSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  // Theme handlers hook - pass null iframeRef in inline mode
  const handlers = useThemeHandlers({
    themeId: themeParams.themeId,
    editThemeToken: themeParams.editThemeToken,
    iframeRef: editorMode === "iframe" ? iframeRef : null,
    refreshThemeColors,
    refreshThemeFonts,
    refreshThemeIcons,
  });

  // Sync URL to storage on mount (only in non-frame mode)
  useEffect(() => {
    if (!isFrameMode) {
      syncUrlToStorage();
    }
  }, [isFrameMode]);

  // Listen for upload icon requests from edit panel IconFields
  useEffect(() => {
    return subscribeToUploadIconRequest(() => {
      setShowIconPicker(true);
    });
  }, []);

  // Determine editor mode on mount by checking CSP headers
  useEffect(() => {
    if (isFrameMode) return; // We're inside iframe, don't check

    const checkEditorMode = async () => {
      const canFrame = await canUseIframe();
      setEditorMode(canFrame ? "iframe" : "inline");
    };

    checkEditorMode();
  }, [isFrameMode]);

  // Validate editThemeToken on load; auto-exit if expired/invalid. V2
  // tokens are implicitly validated on the first GET /versions/:version
  // the ThemeManager issues (server returns 401) so we don't need a
  // separate pre-flight in V2 mode — validateEditThemeToken is V1-shaped.
  useEffect(() => {
    if (isFrameMode) return;
    if (themeParams.previewMode) return;
    if (!themeParams.themeId || !themeParams.editThemeToken) return;
    // V2 target: skip V1 validator (no `themeId` on the server side).
    if (themeParams.appId) return;

    let cancelled = false;

    validateEditThemeToken({
      themeId: themeParams.themeId,
      editThemeToken: themeParams.editThemeToken,
    }).then((valid) => {
      if (cancelled) return;
      if (!valid) {
        clearThemeParams();
        clearUrlParams();
        window.location.reload();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    isFrameMode,
    themeParams.themeId,
    themeParams.editThemeToken,
    themeParams.previewMode,
    themeParams.origin,
    themeParams.appId,
  ]);

  // URL sync: Listen to iframe navigation and update parent URL
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current) return;

    try {
      const iframeUrl = new URL(
        iframeRef.current.contentWindow?.location.href || ""
      );
      const parentUrl = new URL(window.location.href);

      // Update parent URL path and search params (except our editor params)
      parentUrl.pathname = iframeUrl.pathname;

      // Copy search params from iframe, excluding frameMode
      const iframeParams = new URLSearchParams(iframeUrl.search);
      const parentParams = new URLSearchParams();

      // Keep our theme params (both V1 `themeId` shape and V2
      // `appId`+`scope`+`version` shape are preserved; iframe nav
      // shouldn't drop any of them — especially `version`, which binds
      // every Edit live write).
      if (themeParams.themeId) parentParams.set("themeId", themeParams.themeId);
      if (themeParams.editMode) parentParams.set("editMode", "true");
      if (themeParams.editThemeToken)
        parentParams.set("editThemeToken", themeParams.editThemeToken);
      if (themeParams.appId) parentParams.set("appId", themeParams.appId);
      if (themeParams.scope) parentParams.set("scope", themeParams.scope);
      if (themeParams.version) parentParams.set("version", themeParams.version);

      // Copy other params from iframe (except frameMode / editor identity)
      iframeParams.forEach((value, key) => {
        if (
          key !== "frameMode" &&
          key !== "themeId" &&
          key !== "editMode" &&
          key !== "editThemeToken" &&
          key !== "appId" &&
          key !== "scope" &&
          key !== "version"
        ) {
          parentParams.set(key, value);
        }
      });

      parentUrl.search = parentParams.toString();

      // Update browser URL without reload
      window.history.replaceState({}, "", parentUrl.toString());
    } catch (e) {
      // Cross-origin iframe access will fail - that's okay
      console.debug("Could not access iframe URL (cross-origin):", e);
    }
  }, [themeParams]);

  // Preview mode: simplified read-only banner
  if (themeParams.previewMode) {
    const handleExitPreview = () => {
      clearThemeParams();
      clearUrlParams();
      // Stay on the current storefront page (edit params already stripped).
      window.location.reload();
    };

    return (
      <>
        <style>{`
          body { padding-top: 60px !important; }
        `}</style>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 60,
            background: "#f5f5f7",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            padding: "0 20px",
            gap: 16,
            zIndex: 1000001,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Eye size={18} color="white" />
            </div>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1a1a2e" }}>
              Theme Preview
            </span>
          </div>

          {/* Theme Name */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#999", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Theme:
            </span>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#1a1a2e" }}>
              {themeName}
            </span>
            {theme?.isPublished && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#22c55e",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#22c55e",
                  }}
                />
                Live
              </span>
            )}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Exit */}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<LogOut size={14} />}
            onClick={handleExitPreview}
          >
            Exit Preview
          </Button>
        </div>
      </>
    );
  }

  // Show loading state while determining editor mode
  if (editorMode === "checking") {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          background: "#f5f5f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          zIndex: 999999,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: 14,
          color: "#666",
        }}
      >
        <Loader size={16} className="bc-theme-editor-spinner" />
        <span>Initializing Theme Editor...</span>
        <style>{`
          @keyframes bc-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bc-theme-editor-spinner {
            animation: bc-spin 1s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  const handleExitTheme = () => {
    clearThemeParams();
    clearUrlParams();
    // Stay on the current storefront page (edit params already stripped).
    window.location.reload();
  };

  const handleToggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const handleFontSelect = async (font: SelectedFont) => {
    await handlers.handleAddFont(font);
  };

  const handleIconSelect = async (icon: SelectedIcon) => {
    await handlers.handleAddIcon(icon);
  };

  // Inline mode: render only floating UI (top bar + side panel)
  if (editorMode === "inline") {
    return (
      <>
        <style>
          {getThemeEditorStyles({ showSettings, viewMode, editorMode })}
        </style>

        <div className="bc-theme-editor-shell">
          {/* Top Bar */}
          <div className="bc-theme-editor-topbar">
            {/* Logo */}
            <div className="bc-theme-editor-logo">
              <div className="bc-theme-editor-logo-icon">
                <Palette size={18} />
              </div>
              <span className="bc-theme-editor-logo-text">Theme Editor</span>
            </div>

            {/* Theme Name */}
            <div className="bc-theme-editor-theme-info">
              <span className="bc-theme-editor-theme-label">Theme:</span>
              <span className="bc-theme-editor-theme-id">{themeName}</span>
              {theme?.isPublished && (
                <span className="bc-theme-editor-live-indicator">
                  <span className="bc-theme-editor-live-dot" />
                  Live
                </span>
              )}
            </div>

            {/* Spacer */}
            <div className="bc-theme-editor-spacer" />

            {/* Actions */}
            <div className="bc-theme-editor-actions">
              <Button
                variant="outline"
                size="sm"
                pill
                leftIcon={<Settings size={14} />}
                onClick={handleToggleSettings}
              >
                Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<LogOut size={14} />}
                onClick={handleExitTheme}
              >
                Exit Theme
              </Button>
            </div>
          </div>

          {/* Side Panel */}
          {showSettings && (
            <SettingsPanel
              onClose={() => setShowSettings(false)}
              successMessage={handlers.successMessage}
              error={handlers.error}
            >
            <ColorSection
              themeColors={themeColors}
              isExpanded={expandedSection === "colors"}
              onToggle={() => toggleSection("colors")}
              isLoading={handlers.isLoading}
              isAddingColor={handlers.isAddingColor}
              setIsAddingColor={handlers.setIsAddingColor}
              newColorKey={handlers.newColorKey}
              setNewColorKey={handlers.setNewColorKey}
              newColorValue={handlers.newColorValue}
              setNewColorValue={handlers.setNewColorValue}
              onAddColor={handlers.handleAddColor}
              onUpdateColor={handlers.handleUpdateColor}
              onRenameColor={handlers.handleRenameColor}
              onDeleteColor={handlers.handleDeleteColor}
              studioLockedColorKeys={studioLockedColorKeys}
            />

              <FontSection
                themeFonts={themeFonts}
                isExpanded={expandedSection === "fonts"}
                onToggle={() => toggleSection("fonts")}
                isLoading={handlers.isLoading}
                onDeleteFont={handlers.handleDeleteFont}
                onOpenFontPicker={() => setShowFontPicker(true)}
                studioLockedFontKeys={studioLockedFontKeys}
              />

              <IconSection
                themeIcons={themeIcons}
                isExpanded={expandedSection === "icons"}
                onToggle={() => toggleSection("icons")}
                isLoading={handlers.isLoading}
                onDeleteIcon={handlers.handleDeleteIcon}
                onOpenIconPicker={() => setShowIconPicker(true)}
              />
            </SettingsPanel>
          )}

          {/* Font Picker Modal */}
          <FontPickerModal
            isOpen={showFontPicker}
            onClose={() => setShowFontPicker(false)}
            onSelect={handleFontSelect}
            isLoading={handlers.isLoading}
          />

          {/* Icon Picker Modal */}
          <IconPickerModal
            isOpen={showIconPicker}
            onClose={() => setShowIconPicker(false)}
            onSelect={handleIconSelect}
            isLoading={handlers.isLoading}
          />

          {/* Delete Confirmation Dialog */}
          {handlers.pendingDelete && (
            <ConfirmDialog
              title={`Delete ${handlers.pendingDelete.type}`}
              message={`Are you sure you want to delete "${handlers.pendingDelete.key}"? This action cannot be undone.`}
              confirmLabel="Delete"
              cancelLabel="Cancel"
              onConfirm={handlers.confirmDelete}
              onCancel={handlers.cancelDelete}
              isLoading={handlers.isLoading}
            />
          )}
        </div>
      </>
    );
  }

  // Iframe mode: full shell with iframe preview
  return (
    <>
      <style>
        {getThemeEditorStyles({ showSettings, viewMode, editorMode })}
      </style>

      <div className="bc-theme-editor-shell">
        {/* Top Bar */}
        <div className="bc-theme-editor-topbar">
          {/* Logo */}
          <div className="bc-theme-editor-logo">
            <div className="bc-theme-editor-logo-icon">
              <Palette size={18} />
            </div>
            <span className="bc-theme-editor-logo-text">Theme Editor</span>
          </div>

          {/* Theme Name */}
          <div className="bc-theme-editor-theme-info">
            <span className="bc-theme-editor-theme-label">Theme:</span>
            <span className="bc-theme-editor-theme-id">{themeName}</span>
            {theme?.isPublished && (
              <span className="bc-theme-editor-live-indicator">
                <span className="bc-theme-editor-live-dot" />
                Live
              </span>
            )}
          </div>

          {/* Viewport Toggle */}
          <div className="bc-theme-editor-viewport-toggle">
            <Button
              variant={viewMode === "desktop" ? "primary" : "icon"}
              size="sm"
              onClick={() => setViewMode("desktop")}
              aria-label="Desktop view"
            >
              <Monitor size={16} />
            </Button>
            <Button
              variant={viewMode === "mobile" ? "primary" : "icon"}
              size="sm"
              onClick={() => setViewMode("mobile")}
              aria-label="Mobile view"
            >
              <Smartphone size={16} />
            </Button>
          </div>

          {/* Spacer */}
          <div className="bc-theme-editor-spacer" />

          {/* Actions */}
          <div className="bc-theme-editor-actions">
            <Button
              variant="outline"
              size="sm"
              pill
              leftIcon={<Settings size={14} />}
              onClick={handleToggleSettings}
            >
              Settings
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<LogOut size={14} />}
              onClick={handleExitTheme}
            >
              Exit Theme
            </Button>
          </div>
        </div>

        {/* Preview Area with Iframe */}
        <div className="bc-theme-editor-preview">
          <div className="bc-theme-editor-preview-container">
            <div className="bc-theme-editor-iframe-wrapper">
              <iframe
                ref={iframeRef}
                className="bc-theme-editor-iframe"
                src={window.location.href}
                onLoad={handleIframeLoad}
                title="Theme Preview"
              />
            </div>
          </div>
        </div>

        {/* Side Panel */}
        {showSettings && (
          <SettingsPanel
            onClose={() => setShowSettings(false)}
            successMessage={handlers.successMessage}
            error={handlers.error}
          >
            <ColorSection
              themeColors={themeColors}
              isExpanded={expandedSection === "colors"}
              onToggle={() => toggleSection("colors")}
              isLoading={handlers.isLoading}
              isAddingColor={handlers.isAddingColor}
              setIsAddingColor={handlers.setIsAddingColor}
              newColorKey={handlers.newColorKey}
              setNewColorKey={handlers.setNewColorKey}
              newColorValue={handlers.newColorValue}
              setNewColorValue={handlers.setNewColorValue}
              onAddColor={handlers.handleAddColor}
              onUpdateColor={handlers.handleUpdateColor}
              onRenameColor={handlers.handleRenameColor}
              onDeleteColor={handlers.handleDeleteColor}
            />

            <FontSection
              themeFonts={themeFonts}
              isExpanded={expandedSection === "fonts"}
              onToggle={() => toggleSection("fonts")}
              isLoading={handlers.isLoading}
              onDeleteFont={handlers.handleDeleteFont}
              onOpenFontPicker={() => setShowFontPicker(true)}
            />

            <IconSection
              themeIcons={themeIcons}
              isExpanded={expandedSection === "icons"}
              onToggle={() => toggleSection("icons")}
              isLoading={handlers.isLoading}
              onDeleteIcon={handlers.handleDeleteIcon}
              onOpenIconPicker={() => setShowIconPicker(true)}
            />
          </SettingsPanel>
        )}

        {/* Font Picker Modal */}
        <FontPickerModal
          isOpen={showFontPicker}
          onClose={() => setShowFontPicker(false)}
          onSelect={handleFontSelect}
          isLoading={handlers.isLoading}
        />

        {/* Icon Picker Modal */}
        <IconPickerModal
          isOpen={showIconPicker}
          onClose={() => setShowIconPicker(false)}
          onSelect={handleIconSelect}
          isLoading={handlers.isLoading}
        />

        {/* Delete Confirmation Dialog */}
        {handlers.pendingDelete && (
          <ConfirmDialog
            title={`Delete ${handlers.pendingDelete.type}`}
            message={`Are you sure you want to delete "${handlers.pendingDelete.key}"? This action cannot be undone.`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={handlers.confirmDelete}
            onCancel={handlers.cancelDelete}
            isLoading={handlers.isLoading}
          />
        )}
      </div>
    </>
  );
};

export const ThemeManager: React.FC<ThemeManagerProps> = () => {
  const themeParams = useWidgetThemeParams();
  const isFrameMode = getIsFrameMode();

  // A theme target is present if EITHER V1 `themeId` OR V2 `appId` is set.
  // The editor only shells up once a target exists.
  const hasThemeTarget = !!(themeParams.themeId || themeParams.appId);

  // Don't render if no theme target (V1 `themeId` or V2 `appId`).
  if (!hasThemeTarget) {
    return null;
  }

  // If we're in frameMode, don't render the editor shell (let the page render normally)
  if (isFrameMode) {
    return null;
  }

  return (
    <ThemeManagerContent
      themeParams={themeParams}
      isFrameMode={isFrameMode}
    />
  );
};
